import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Calendar, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import NewsletterBox from "@/components/NewsletterBox";
import { FALLBACK_IMAGE } from "@/data/constants";

const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return "";
  }
};

export default function NewsletterArchive() {
  useSeo({
    title: "Newsletter Archive",
    description:
      "Read past issues of the Wolfdog News newsletter — stories, legislation updates, and community news, all in one place.",
  });
  const [list, setList] = useState([]);

  useEffect(() => {
    api.get("/newsletters", { params: { status: "published" } }).then((r) => setList(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      <div className="relative h-64 bg-[#0A192F]">
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-4 w-full">
            <div className="flex items-center gap-3 mb-3">
              <Mail className="w-7 h-7 text-[#D4AF37]" />
              <span className="overline text-[#D4AF37]">The Wolfdog News</span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tight">
              Newsletter Archive
            </h1>
            <p className="mt-3 text-white/70 max-w-2xl">
              Every issue we send lives here — a permanent, searchable record of wolfdog news, legislation, and community updates.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8">
          {list.length ? (
            <div className="space-y-6" data-testid="newsletter-archive-list">
              {list.map((n) => (
                <Link
                  key={n.id}
                  to={`/newsletter/${n.slug}`}
                  data-testid={`newsletter-card-${n.slug}`}
                  className="group flex flex-col sm:flex-row gap-5 bg-white border border-[#0A192F]/10 hover:border-[#D4AF37] hover:-translate-y-1 transition-all overflow-hidden"
                >
                  <div className="sm:w-56 h-44 sm:h-auto shrink-0 bg-[#0A192F]">
                    <img src={n.image_url || FALLBACK_IMAGE} alt={n.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-5 flex flex-col">
                    <span className="overline text-[#8B6D1F] flex items-center gap-2">
                      <Calendar className="w-3.5 h-3.5" /> {fmt(n.published_at)}
                    </span>
                    <h2 className="font-display text-xl font-bold text-[#0A192F] mt-2 group-hover:text-[#8B6D1F] transition-colors">
                      {n.title}
                    </h2>
                    <p className="text-sm text-[#4A5568] mt-2 line-clamp-2 leading-relaxed">{n.excerpt}</p>
                    <span className="overline text-[#8B6D1F] mt-3 inline-flex items-center gap-1 group-hover:underline">
                      Read Issue <ArrowRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-[#4A5568]">No newsletters published yet. Check back soon.</p>
          )}
        </div>

        <aside className="lg:col-span-4">
          <NewsletterBox />
        </aside>
      </div>
    </div>
  );
}
